<template>
  <div>
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Conciliações Bancárias
          </span>
          <span class="text-muted mt-3 font-weight-bold font-size-sm">
            Gerencie as conciliações importadas via arquivo OFX
          </span>
        </h3>
        <div class="card-toolbar">
          <router-link
            :to="{ name: 'conciliacaoBancariaNova' }"
            class="btn btn-dark font-weight-bolder font-size-sm"
          >
            <Upload :size="16" class="mr-1" />
            Importar OFX
          </router-link>
        </div>
      </div>

      <!-- Filtros -->
      <div class="card-body pt-0 pb-2">
        <b-row class="mb-4">
          <b-col md="3">
            <b-form-group label="Banco" label-for="filtro_banco">
              <b-form-input
                id="filtro_banco"
                v-model="filtros.banco"
                placeholder="Filtrar por banco"
                @input="filtrar"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Status" label-for="filtro_status">
              <b-form-select id="filtro_status" v-model="filtros.status" @change="filtrar">
                <b-form-select-option value="">Todos</b-form-select-option>
                <b-form-select-option value="pendente">Pendente</b-form-select-option>
                <b-form-select-option value="parcial">Parcial</b-form-select-option>
                <b-form-select-option value="concluido">Concluído</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Data início" label-for="filtro_data_inicio">
              <b-form-input
                id="filtro_data_inicio"
                type="date"
                v-model="filtros.data_inicio"
                @change="filtrar"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Data fim" label-for="filtro_data_fim">
              <b-form-input
                id="filtro_data_fim"
                type="date"
                v-model="filtros.data_fim"
                @change="filtrar"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Tabela de conciliações -->
        <div class="table-responsive" v-if="!isLoading">
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Banco</th>
                <th>Conta</th>
                <th>Lançamento</th>
                <th>Período OFX</th>
                <th>Total Lançamentos</th>
                <th>Conciliados</th>
                <th>% Conclusão</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="conciliacoesFiltradas.length === 0">
                <td colspan="9" class="text-center text-muted py-4">
                  Nenhuma conciliação encontrada.
                </td>
              </tr>
              <tr
                v-for="(item, index) in conciliacoesFiltradas"
                :key="'conciliacao-' + index"
              >
                <td>
                  <div class="font-weight-bold">{{ item.banco_nome }}</div>
                  <small class="text-muted">{{ item.banco_codigo }}</small>
                </td>
                <td>
                  <span class="text-muted">Ag: {{ item.agencia }}</span
                  ><br />
                  <span class="text-muted">Cc: {{ item.conta }}</span>
                </td>
                <td>{{ formatDate(item.data_importacao) }}</td>
                <td>
                  <small>
                    {{ formatDate(item.data_inicio_ofx) }} até
                    {{ formatDate(item.data_fim_ofx) }}
                  </small>
                </td>
                <td class="text-center">{{ item.total_lancamentos }}</td>
                <td class="text-center">{{ item.total_conciliados }}</td>
                <td style="min-width: 140px">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 mr-2">
                      <b-progress
                        :value="item.percentual"
                        :max="100"
                        :variant="getProgressVariant(item.percentual)"
                        height="10px"
                      />
                    </div>
                    <span
                      class="font-weight-bold"
                      :class="getTextVariant(item.percentual)"
                      style="min-width: 40px"
                    >
                      {{ item.percentual }}%
                    </span>
                  </div>
                </td>
                <td>
                  <span :class="getBadgeClass(item.status)">
                    {{ getLabelStatus(item.status) }}
                  </span>
                </td>
                <td>
                  <div class="d-flex" style="gap: 6px">
                    <a
                      href="#"
                      @click.prevent="abrirDetalheConciliacao(item)"
                      class="btn btn-sm btn-info btn-hover-info btn-icon"
                      v-b-tooltip.hover.bottom="'Ver detalhe'"
                    >
                      <Eye :size="16" />
                    </a>
                    <a
                      v-if="item.arquivo_ofx_url"
                      :href="item.arquivo_ofx_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-secondary btn-hover-secondary btn-icon"
                      v-b-tooltip.hover.bottom="'Baixar arquivo OFX'"
                    >
                      <Download :size="16" />
                    </a>
                    <button
                      @click="excluirConciliacao(item)"
                      class="btn btn-sm btn-danger btn-hover-danger btn-icon"
                      v-b-tooltip.hover.bottom="'Excluir conciliação'"
                    >
                      <TrashCanIcon :size="16" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="isLoading" class="d-flex justify-content-center py-5">
          <b-spinner variant="primary" label="Carregando..." />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SistemaMixin } from "@/modules/Application";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";
import Eye from "vue-material-design-icons/Eye.vue";
import Download from "vue-material-design-icons/Download.vue";
import Upload from "vue-material-design-icons/Upload.vue";

export default {
  name: "app-conciliacao-bancaria-lista",
  mixins: [SistemaMixin],
  components: {
    TrashCanIcon,
    Eye,
    Download,
    Upload,
  },
  data() {
    return {
      isLoading: false,
      conciliacoes: [],
      filtros: {
        banco: "",
        status: "",
        data_inicio: "",
        data_fim: "",
      },
    };
  },
  computed: {
    conciliacoesFiltradas() {
      return this.conciliacoes.filter((item) => {
        const matchBanco =
          !this.filtros.banco ||
          (item.banco_nome || "")
            .toLowerCase()
            .includes(this.filtros.banco.toLowerCase());
        const matchStatus =
          !this.filtros.status || item.status === this.filtros.status;
        const matchInicio =
          !this.filtros.data_inicio ||
          item.data_importacao >= this.filtros.data_inicio;
        const matchFim =
          !this.filtros.data_fim ||
          item.data_importacao <= this.filtros.data_fim;
        return matchBanco && matchStatus && matchInicio && matchFim;
      });
    },
  },
  mounted() {
    this.fetchConciliacoes();
  },
  methods: {
    async fetchConciliacoes() {
      this.isLoading = true;
      try {
        const { data } = await this.$http.post("/conciliacao-bancaria/lista");
        // Aceita ambos os formatos: array direto ou { data: [...] }
        this.conciliacoes = Array.isArray(data) ? data : (data.data ?? []);
      } catch (error) {
        this.$toast.error("Erro ao carregar as conciliações bancárias.");
      } finally {
        this.isLoading = false;
      }
    },
    filtrar() {
      // Reatividade via computed já aplica os filtros automaticamente
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString("pt-BR");
    },
    getProgressVariant(percent) {
      if (percent >= 100) return "success";
      if (percent >= 50) return "warning";
      return "danger";
    },
    getTextVariant(percent) {
      if (percent >= 100) return "text-success";
      if (percent >= 50) return "text-warning";
      return "text-danger";
    },
    getBadgeClass(status) {
      const map = {
        pendente: "badge badge-warning",
        parcial: "badge badge-info",
        concluido: "badge badge-success",
      };
      return map[status] || "badge badge-secondary";
    },
    getLabelStatus(status) {
      const map = {
        pendente: "Pendente",
        parcial: "Parcial",
        concluido: "Concluído",
      };
      return map[status] || status;
    },
    excluirConciliacao(item) {
      this.$swal
        .fire({
          icon: "warning",
          title: `Excluir a conciliação do banco ${item.banco_nome}?`,
          text: "Esta ação não poderá ser desfeita.",
          showCancelButton: true,
          confirmButtonText: "Sim, excluir",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#f64e60",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await this.$http.post("/conciliacao-bancaria/delete", {
                id_conciliacao: item.id_conciliacao,
              });
              this.$toast.success("Conciliação excluída com sucesso!");
              this.fetchConciliacoes();
            } catch (error) {
              this.$toast.error("Erro ao excluir a conciliação.");
            }
          }
        });
    },
    abrirDetalheConciliacao(item) {
      this.$router.push({ name: 'conciliacaoBancariaDetalhe', params: { id: item.id_conciliacao } });
    },
  },
};
</script>
